import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Trust Over Blame',
      description: 'We believe safety improves when people feel safe to report concerns, admit mistakes, and ask questions without fear of punishment.'
    },
    {
      icon: 'ri-tools-line',
      title: 'Practical Over Theoretical',
      description: 'Our solutions fit real work environments. We focus on what actually works on the floor, not just what looks good on paper.'
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Prevention Over Reaction',
      description: 'Leading indicators, proactive behaviors, and risk recognition prevent incidents before they happen — that\'s where we focus our energy.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#14173A] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37] to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37] mb-6">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wide">About Horizon Safety</span>
          </div>
          
          <h1 className="text-6xl font-bold text-white mb-6">
            Safety Built on Real-World Experience
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Practical safety solutions from someone who lives it every day.
          </p>
          
          <div className="flex items-center justify-center gap-3 text-gray-400 text-sm">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#D4AF37]">About</span>
          </div>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-24 bg-[#F7F7F9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/c33c1fda10e5dae48518c934c3293640.jpg"
                  alt="Safety leadership"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#14173A]/30 to-transparent"></div>
              </div>
            </div>
            
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 mb-6">
                <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wide">Our Story</span>
              </div>
              
              <h2 className="text-4xl font-bold text-[#14173A] mb-6 leading-tight">
                Built by Someone Who Understands Your Challenges
              </h2>
              
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p>
                  Horizon Safety was founded to help organizations build compliant, proactive safety systems that actually work in the real world — not just on paper.
                </p>
                
                <p>
                  Our founder is currently a Safety Director at a manufacturing facility, bringing firsthand experience from the front lines of safety management. This isn't theoretical consulting — it's practical guidance from someone who deals with the same challenges you face every day.
                </p>
                
                <div className="bg-white rounded-2xl p-8 border-l-4 border-[#D4AF37] shadow-md my-8">
                  <p className="text-lg italic text-[#14173A] font-medium">
                    "I built the Forward Safety System™ because I needed it myself. When you're managing safety in a real operation, you need systems that are practical, scalable, and focused on prevention — not just compliance paperwork."
                  </p>
                </div>
                
                <p>
                  We work with organizations across Minnesota and Wisconsin (and remotely beyond) in manufacturing, food production, warehousing, construction, and other industries. Our approach is simple: get compliant first, then build the culture and systems that prevent incidents before they happen.
                </p>
                
                <p>
                  Whether you're a growing business needing to establish your safety foundation, or an established operation ready to move from reactive to proactive safety management, we meet you where you are and help you build forward.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#14173A] mb-4">Our Values</h2>
            <div className="w-32 h-1 bg-[#D4AF37] mx-auto"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-10">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-[#F7F7F9] rounded-3xl p-10 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-[#D4AF37] text-6xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-[#14173A] mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-24 bg-[#14173A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Serving Minnesota, Wisconsin & Beyond
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                We provide on-site and remote safety consulting services to organizations across the Upper Midwest and beyond.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-[#D4AF37] text-3xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Primary Service Area</h3>
                    <p className="text-gray-300">Minnesota and Wisconsin — on-site visits and in-person workshops available</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <i className="ri-global-line text-[#D4AF37] text-3xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Remote Support</h3>
                    <p className="text-gray-300">Virtual consulting, coaching, and training available nationwide</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-[#D4AF37] text-3xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Industries We Serve</h3>
                    <p className="text-gray-300">Manufacturing, food production, warehousing, construction, field services, facilities management, and growing businesses</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20diverse%20team%20of%20professionals%20in%20safety%20meeting%2C%20collaborative%20discussion%20around%20table%20with%20laptops%20and%20documents%2C%20bright%20modern%20office%20with%20large%20windows%2C%20professional%20business%20photography%20showing%20teamwork%20and%20engagement%2C%20warm%20natural%20lighting&width=800&height=600&seq=about002&orientation=landscape"
                  alt="Team collaboration"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#D4AF37] to-[#C9A832]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-[#14173A] mb-6">
            Let's Build Your Safety System Together
          </h2>
          <p className="text-xl text-[#14173A]/80 mb-10">
            Ready to move from compliance to culture? Let's talk about your goals.
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-3 px-10 py-5 bg-white text-[#14173A] rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-300 shadow-xl whitespace-nowrap cursor-pointer"
          >
            Request a Consultation
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
